/**
 * Context panel showing current entity details, quick actions, and recent activity
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  List,
  Tag,
  Statistic,
  Row,
  Col,
  Empty,
  Collapse,
  Badge,
  Tooltip,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Spin,
  Descriptions,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { getCurrentUserId } from "@/hooks/useCurrentUser";
import {
  EditOutlined,
  PhoneOutlined,
  FileAddOutlined,
  BarChartOutlined,
  LineChartOutlined,
  StarFilled,
  PlusOutlined,
  DeleteOutlined,
  BankOutlined,
  UserOutlined,
  DollarOutlined,
  CheckCircleFilled,
  HistoryOutlined,
  EyeOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useBusinessActions } from "@/hooks/useBusinessActions";
import { RelatedDocuments } from "@/components/documents/RelatedDocuments";
import { useOne } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { useContextStore } from "@/hooks/useContextStore";

const { Text, Title } = Typography;

// Entity name cache type
type EntityNameCache = {
  accounts: Record<string, string>;
  contacts: Record<string, string>;
  opportunities: Record<string, string>;
};

interface ContextPanelProps {
  contextId?: string;
}

export const ContextPanel: React.FC<ContextPanelProps> = ({ contextId }) => {
  const navigate = useNavigate();
  const {
    currentAccountId,
    cacheAccount,
    contexts,
    activeContextId,
    setActiveContext,
    removeContext,
    removeEntityFromContext,
    setCurrentAccount,
    entityHistory,
    getCurrentEntity,
  } = useContextStore();

  // Get current entity for business actions
  const currentEntity = getCurrentEntity();
  const currentModule = currentEntity?.module || "Accounts";

  // Fetch business actions for current module
  const { actions: businessActions, loading: actionsLoading } =
    useBusinessActions(currentModule);

  const [expandedContextId, setExpandedContextId] = useState<string | null>(
    null
  );

  // Modal states for Quick Actions
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityForm] = Form.useForm();

  const [pipelineModalOpen, setPipelineModalOpen] = useState(false);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [pipelineData, setPipelineData] = useState<any>(null);

  const [forecastModalOpen, setForecastModalOpen] = useState(false);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastData, setForecastData] = useState<any>(null);

  // Handler for Log Activity
  const handleLogActivity = async (values: any) => {
    setActivityLoading(true);
    try {
      const eventData = {
        subject: values.subject,
        activitytype: values.activitytype,
        date_start: values.date_start.format("YYYY-MM-DD"),
        time_start: values.date_start.format("HH:mm:ss"),
        due_date: values.date_start.format("YYYY-MM-DD"),
        time_end: values.date_start.add(1, "hour").format("HH:mm:ss"),
        eventstatus: "Held",
        visibility: "Private",
        description: values.description || "",
        assigned_user_id: getCurrentUserId(),
      };

      // Add rel_id if we have a current account
      if (currentAccountId) {
        (eventData as any).rel_id = currentAccountId;
      }

      await axios.post("/api/direct/create", {
        module: "cbCalendar",
        data: eventData,
      });

      message.success("Activity logged successfully");
      setActivityModalOpen(false);
      activityForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.detail || "Failed to log activity");
    } finally {
      setActivityLoading(false);
    }
  };

  // Handler for View Pipeline
  const handleViewPipeline = async () => {
    setPipelineLoading(true);
    setPipelineModalOpen(true);
    try {
      // Query opportunities for the current account
      let query =
        "SELECT id, potentialname, amount, sales_stage, closingdate FROM Potentials";
      if (currentAccountId) {
        query += ` WHERE potentialname LIKE '%${account?.accountname || ""}%'`;
      }
      query += " LIMIT 50;";

      const response = await axios.post("/api/direct/query", { query });
      const opportunities = response.data.result || [];

      // Calculate pipeline summary
      const summary = {
        totalDeals: opportunities.length,
        totalValue: opportunities.reduce(
          (sum: number, opp: any) => sum + parseFloat(opp.amount || 0),
          0
        ),
        byStage: {} as Record<string, { count: number; value: number }>,
        opportunities,
      };

      opportunities.forEach((opp: any) => {
        const stage = opp.sales_stage || "Unknown";
        if (!summary.byStage[stage]) {
          summary.byStage[stage] = { count: 0, value: 0 };
        }
        summary.byStage[stage].count++;
        summary.byStage[stage].value += parseFloat(opp.amount || 0);
      });

      setPipelineData(summary);
    } catch (error: any) {
      message.error(error.response?.data?.detail || "Failed to load pipeline");
    } finally {
      setPipelineLoading(false);
    }
  };

  // Handler for Forecast Report
  const handleForecastReport = async () => {
    setForecastLoading(true);
    setForecastModalOpen(true);
    try {
      // Get revenue pipeline data
      const params: any = {};
      if (currentAccountId) {
        params.account_id = currentAccountId;
      }

      const response = await axios.post("/api/direct/revenue-pipeline", params);
      setForecastData(response.data.result || response.data);
    } catch (error: any) {
      message.error(error.response?.data?.detail || "Failed to load forecast");
    } finally {
      setForecastLoading(false);
    }
  };

  // Cache for entity names
  const [entityNames, setEntityNames] = useState<EntityNameCache>({
    accounts: {},
    contacts: {},
    opportunities: {},
  });

  // Fetch entity names for all contexts
  useEffect(() => {
    const fetchEntityNames = async () => {
      const allAccountIds: string[] = [];
      const allContactIds: string[] = [];
      const allOpportunityIds: string[] = [];

      // Collect all entity IDs from all contexts
      contexts.forEach((ctx) => {
        if (ctx.entities) {
          allAccountIds.push(...(ctx.entities.accounts || []));
          allContactIds.push(...(ctx.entities.contacts || []));
          allOpportunityIds.push(...(ctx.entities.opportunities || []));
        }
      });

      // Fetch account names
      const uniqueAccountIds = [...new Set(allAccountIds)];
      if (uniqueAccountIds.length > 0) {
        try {
          const response = await axios.post("/api/direct/query", {
            query: `SELECT id, accountname FROM Accounts WHERE id = '${uniqueAccountIds.join(
              "' OR id = '"
            )}' LIMIT 100;`,
          });
          const accounts = response.data?.result || [];
          const accountMap = accounts.reduce(
            (
              acc: Record<string, string>,
              a: { id: string; accountname: string }
            ) => {
              acc[a.id] = a.accountname;
              return acc;
            },
            {}
          );
          setEntityNames((prev) => ({
            ...prev,
            accounts: { ...prev.accounts, ...accountMap },
          }));
        } catch (error) {
          console.error("Failed to fetch account names:", error);
        }
      }

      // Fetch contact names
      const uniqueContactIds = [...new Set(allContactIds)];
      if (uniqueContactIds.length > 0) {
        try {
          const response = await axios.post("/api/direct/query", {
            query: `SELECT id, firstname, lastname FROM Contacts WHERE id = '${uniqueContactIds.join(
              "' OR id = '"
            )}' LIMIT 100;`,
          });
          const contacts = response.data?.result || [];
          const contactMap = contacts.reduce(
            (
              acc: Record<string, string>,
              c: { id: string; firstname: string; lastname: string }
            ) => {
              acc[c.id] = `${c.firstname || ""} ${c.lastname || ""}`.trim();
              return acc;
            },
            {}
          );
          setEntityNames((prev) => ({
            ...prev,
            contacts: { ...prev.contacts, ...contactMap },
          }));
        } catch (error) {
          console.error("Failed to fetch contact names:", error);
        }
      }

      // Fetch opportunity names
      const uniqueOpportunityIds = [...new Set(allOpportunityIds)];
      if (uniqueOpportunityIds.length > 0) {
        try {
          const response = await axios.post("/api/direct/query", {
            query: `SELECT id, potentialname FROM Potentials WHERE id = '${uniqueOpportunityIds.join(
              "' OR id = '"
            )}' LIMIT 100;`,
          });
          const opportunities = response.data?.result || [];
          const oppMap = opportunities.reduce(
            (
              acc: Record<string, string>,
              o: { id: string; potentialname: string }
            ) => {
              acc[o.id] = o.potentialname;
              return acc;
            },
            {}
          );
          setEntityNames((prev) => ({
            ...prev,
            opportunities: { ...prev.opportunities, ...oppMap },
          }));
        } catch (error) {
          console.error("Failed to fetch opportunity names:", error);
        }
      }
    };

    fetchEntityNames();
  }, [contexts]);

  // Get context info if contextId provided
  const context = contextId ? contexts.get(contextId) : null;

  // Get entity counts for a context
  const getEntityCounts = (ctx: typeof context) => {
    if (!ctx?.entities)
      return { accounts: 0, contacts: 0, opportunities: 0, tickets: 0 };
    return {
      accounts: ctx.entities.accounts?.length || 0,
      contacts: ctx.entities.contacts?.length || 0,
      opportunities: ctx.entities.opportunities?.length || 0,
      tickets: ctx.entities.tickets?.length || 0,
    };
  };

  // Fetch current account
  const { data: accountData, isLoading: accountLoading } = useOne({
    resource: "accounts",
    id: currentAccountId || "",
    queryOptions: {
      enabled: !!currentAccountId,
    },
  });

  // Cache account when data changes
  React.useEffect(() => {
    if (accountData?.data) {
      cacheAccount(accountData.data as any);
    }
  }, [accountData, cacheAccount]);

  // Opportunities disabled — Potentials module not accessible in this coreBOS instance
  const opportunities: any[] = [];

  const account = accountData?.data;

  // Fetch recent activities (cbCalendar events) for current account
  const [activities, setActivities] = useState<
    Array<{
      id: string;
      subject: string;
      activitytype: string;
      date_start: string;
      time_start: string;
    }>
  >([]);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!currentAccountId) {
        setActivities([]);
        return;
      }
      try {
        // Build full WSID for Account (11x prefix)
        const fullWsid = currentAccountId.includes("x")
          ? currentAccountId
          : `11x${currentAccountId}`;
        const response = await axios.post("/api/direct/query", {
          query: `SELECT id, subject, activitytype, date_start, time_start
                  FROM cbCalendar
                  WHERE rel_id = '${fullWsid}'
                  LIMIT 5;`,
        });
        const data = response.data?.result || [];
        setActivities(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to fetch activities:", error);
        setActivities([]);
      }
    };
    fetchActivities();
  }, [currentAccountId]);

  // Render contexts accordion - defined before early returns so it can be used in all return paths
  const renderContextsAccordion = () => {
    const contextList = Array.from(contexts.values());

    return (
      <Card
        size="small"
        title={
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <span>Contexts</span>
            <Tooltip title="New Context">
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  // TODO: Open new context modal
                }}
              />
            </Tooltip>
          </Space>
        }
        style={{ marginBottom: 16 }}
        styles={{ body: { padding: 0 } }}
      >
        <Collapse
          accordion
          ghost
          activeKey={expandedContextId || undefined}
          onChange={(key) =>
            setExpandedContextId(
              Array.isArray(key) ? key[0] || null : key || null
            )
          }
          items={contextList.map((ctx) => {
            const counts = getEntityCounts(ctx);
            const isActive = ctx.id === activeContextId;
            const totalEntities =
              counts.accounts +
              counts.contacts +
              counts.opportunities +
              counts.tickets;

            return {
              key: ctx.id,
              label: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                  onClick={(e) => {
                    // Clicking the label area activates context for chat
                    e.stopPropagation();
                    setActiveContext(ctx.id);
                  }}
                >
                  <Space size="small">
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: ctx.color,
                        display: "inline-block",
                      }}
                    />
                    <Text strong={isActive}>{ctx.label}</Text>
                    {isActive && (
                      <CheckCircleFilled
                        style={{ color: "#52c41a", fontSize: 12 }}
                      />
                    )}
                  </Space>
                  <Space size={4}>
                    {counts.accounts > 0 && (
                      <Tooltip title={`${counts.accounts} accounts`}>
                        <Badge
                          count={counts.accounts}
                          size="small"
                          style={{ backgroundColor: "#1890ff" }}
                        />
                      </Tooltip>
                    )}
                    {counts.opportunities > 0 && (
                      <Tooltip title={`${counts.opportunities} deals`}>
                        <Badge
                          count={counts.opportunities}
                          size="small"
                          style={{ backgroundColor: "#52c41a" }}
                        />
                      </Tooltip>
                    )}
                    {totalEntities === 0 && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Empty
                      </Text>
                    )}
                  </Space>
                </div>
              ),
              children: (
                <div style={{ paddingLeft: 8 }}>
                  {/* Accounts */}
                  {counts.accounts > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        <BankOutlined /> Accounts
                      </Text>
                      <div style={{ paddingLeft: 16, marginTop: 4 }}>
                        {ctx.entities?.accounts.map((id) => (
                          <Tag
                            key={id}
                            closable
                            color="blue"
                            onClose={() =>
                              removeEntityFromContext(ctx.id, "accounts", id)
                            }
                            style={{ marginBottom: 4, cursor: "pointer" }}
                            onClick={() => navigate(`/accounts/${id}`)}
                          >
                            {entityNames.accounts[id] || id}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Contacts */}
                  {counts.contacts > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        <UserOutlined /> Contacts
                      </Text>
                      <div style={{ paddingLeft: 16, marginTop: 4 }}>
                        {ctx.entities?.contacts.map((id) => (
                          <Tag
                            key={id}
                            closable
                            color="green"
                            onClose={() =>
                              removeEntityFromContext(ctx.id, "contacts", id)
                            }
                            style={{ marginBottom: 4, cursor: "pointer" }}
                            onClick={() => navigate(`/contacts?id=${id}`)}
                          >
                            {entityNames.contacts[id] || id}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Opportunities */}
                  {counts.opportunities > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        <DollarOutlined /> Deals
                      </Text>
                      <div style={{ paddingLeft: 16, marginTop: 4 }}>
                        {ctx.entities?.opportunities.map((id) => (
                          <Tag
                            key={id}
                            closable
                            color="gold"
                            onClose={() =>
                              removeEntityFromContext(
                                ctx.id,
                                "opportunities",
                                id
                              )
                            }
                            style={{ marginBottom: 4, cursor: "pointer" }}
                            onClick={() => navigate(`/opportunities/${id}`)}
                          >
                            {entityNames.opportunities[id] || id}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty state */}
                  {totalEntities === 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      No entities in this context. Add accounts, contacts, or
                      deals from search results.
                    </Text>
                  )}

                  {/* Context actions */}
                  <div
                    style={{
                      marginTop: 8,
                      borderTop: "1px solid #f0f0f0",
                      paddingTop: 8,
                    }}
                  >
                    <Space>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeContext(ctx.id)}
                      >
                        Delete
                      </Button>
                    </Space>
                  </div>
                </div>
              ),
            };
          })}
        />
      </Card>
    );
  };

  // If this is a pipeline context, show pipeline view
  if (context?.type === "pipeline") {
    return (
      <div style={{ padding: 16, height: "100%", overflowY: "auto" }}>
        <Card
          size="small"
          title="Pipeline Overview"
          style={{ marginBottom: 16 }}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text type="secondary">
              Showing deals matching: {context.label}
            </Text>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={12}>
                <Statistic
                  title="Active Deals"
                  value={opportunities.length}
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Total Value"
                  value={opportunities.reduce(
                    (sum, o) => sum + Number(o.amount || 0),
                    0
                  )}
                  prefix="$"
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
            </Row>
          </Space>
        </Card>

        {/* Pipeline Deals List */}
        <Card size="small" title="Deals" styles={{ body: { padding: 0 } }}>
          <List
            size="small"
            dataSource={opportunities}
            renderItem={(opp) => (
              <List.Item style={{ padding: "8px 12px" }}>
                <List.Item.Meta
                  title={
                    <Text ellipsis style={{ maxWidth: 200 }}>
                      {opp.potentialname}
                    </Text>
                  }
                  description={
                    <Space size="small">
                      <Tag color={getStageColor(opp.sales_stage)}>
                        {opp.sales_stage}
                      </Tag>
                      {opp.amount && (
                        <Text type="secondary">
                          ${Number(opp.amount).toLocaleString()}
                        </Text>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
            locale={{ emptyText: "No deals in pipeline" }}
          />
        </Card>
      </div>
    );
  }

  // Always show contexts accordion, even without account selected
  if (!currentAccountId) {
    return (
      <div style={{ padding: 16, height: "100%", overflowY: "auto" }}>
        {/* Contexts Accordion - always visible */}
        {renderContextsAccordion()}

        <Empty
          description={
            context
              ? `Select an item for ${context.label}`
              : "Select an account to see details"
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  if (accountLoading) {
    return (
      <div style={{ padding: 16 }}>
        <Card loading />
      </div>
    );
  }

  return (
    <div style={{ padding: 16, height: "100%", overflowY: "auto" }}>
      {/* Contexts Accordion */}
      {renderContextsAccordion()}

      {/* Entity History */}
      {entityHistory.length > 0 && (
        <Card
          size="small"
          title={
            <Space>
              <HistoryOutlined />
              Recent
            </Space>
          }
          style={{ marginBottom: 16 }}
          styles={{ body: { padding: 0 } }}
        >
          <List
            size="small"
            dataSource={[...entityHistory].reverse()}
            renderItem={(entity) => (
              <List.Item
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  const modulePath = entity.module.toLowerCase();
                  // Set as current context if it's an Account
                  if (entity.module === "Accounts") {
                    setCurrentAccount(entity.id);
                  }
                  // Signal that this is a history navigation (don't collapse panel)
                  window.dispatchEvent(
                    new CustomEvent("entity-history-navigate")
                  );
                  navigate(`/${modulePath}/${entity.id}`);
                }}
              >
                <Space>
                  {entity.module === "Accounts" && (
                    <BankOutlined style={{ color: "#1890ff" }} />
                  )}
                  {entity.module === "Contacts" && (
                    <UserOutlined style={{ color: "#52c41a" }} />
                  )}
                  {entity.module === "Potentials" && (
                    <DollarOutlined style={{ color: "#faad14" }} />
                  )}
                  {!["Accounts", "Contacts", "Potentials"].includes(
                    entity.module
                  ) && <EyeOutlined style={{ color: "#722ed1" }} />}
                  <Text ellipsis style={{ maxWidth: 180 }}>
                    {entity.name}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* Account Header */}
      {account && (
        <div style={{ marginBottom: 16 }}>
          <Space
            align="start"
            style={{ width: "100%", justifyContent: "space-between" }}
          >
            <div>
              <Title level={5} style={{ margin: 0 }}>
                {account.accountname}
              </Title>
              <Text type="secondary">{account.industry}</Text>
            </div>
            <Space>
              <Tooltip title="Clear account selection">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setCurrentAccount(null);
                  }}
                />
              </Tooltip>
              {[1, 2, 3, 4, 5].map((star) => (
                <StarFilled
                  key={star}
                  style={{
                    color:
                      star <= (account.rating ? 5 : 0) ? "#faad14" : "#d9d9d9",
                    fontSize: 12,
                  }}
                />
              ))}
            </Space>
          </Space>

          {/* Key Metrics */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <Statistic
                title="ARR"
                value={account.annual_revenue || 0}
                prefix="$"
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="Employees"
                value={account.employees || 0}
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
          </Row>
        </div>
      )}

      {/* Business Actions (from coreBOS) */}
      {businessActions.length > 0 && (
        <Card
          size="small"
          title={
            <Space>
              <ThunderboltOutlined />
              Quick Actions
            </Space>
          }
          style={{ marginBottom: 16 }}
          styles={{ body: { padding: 8 } }}
          loading={actionsLoading}
        >
          <Space direction="vertical" style={{ width: "100%" }} size="small">
            {businessActions.map((action) => (
              <Button
                key={action.id}
                block
                style={{ textAlign: "left" }}
                onClick={() => {
                  // Handle action execution
                  if (action.url) {
                    // External link or internal route
                    if (action.url.startsWith("http")) {
                      window.open(action.url, "_blank");
                    } else {
                      navigate(action.url);
                    }
                  } else if (action.handler_class && action.handler_path) {
                    // Call backend handler
                    message.info(`Executing ${action.label}...`);
                    // TODO: Implement handler call
                  }
                }}
              >
                {action.label}
              </Button>
            ))}
          </Space>
        </Card>
      )}

      {/* Account Actions */}
      <Card
        size="small"
        title="Account Actions"
        style={{ marginBottom: 16 }}
        styles={{ body: { padding: 8 } }}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="small">
          <Button
            block
            icon={<EditOutlined />}
            style={{ textAlign: "left" }}
            onClick={() =>
              currentAccountId && navigate(`/accounts/${currentAccountId}/edit`)
            }
            disabled={!currentAccountId}
          >
            Edit Account
          </Button>
          <Button
            block
            icon={<PhoneOutlined />}
            style={{ textAlign: "left" }}
            onClick={() => setActivityModalOpen(true)}
          >
            Log Activity
          </Button>
          <Button
            block
            icon={<FileAddOutlined />}
            style={{ textAlign: "left" }}
            onClick={() => {
              // Navigate to create opportunity with account pre-selected
              if (currentAccountId) {
                navigate(`/opportunities/create?account=${currentAccountId}`);
              } else {
                navigate("/opportunities/create");
              }
            }}
          >
            New Opportunity
          </Button>
          <Button
            block
            icon={<BarChartOutlined />}
            style={{ textAlign: "left" }}
            onClick={handleViewPipeline}
          >
            View Pipeline
          </Button>
          <Button
            block
            icon={<LineChartOutlined />}
            style={{ textAlign: "left" }}
            onClick={handleForecastReport}
          >
            Forecast Report
          </Button>
        </Space>
      </Card>

      {/* Recent Opportunities */}
      <Card
        size="small"
        title={`Opportunities (${opportunities.length})`}
        style={{ marginBottom: 16 }}
        styles={{ body: { padding: 0 } }}
      >
        <List
          size="small"
          dataSource={opportunities}
          renderItem={(opp: any) => (
            <List.Item
              style={{ padding: "8px 12px" }}
              actions={[
                <Tooltip title="View" key="view">
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/opportunities/${opp.id}`)}
                  />
                </Tooltip>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Text ellipsis style={{ maxWidth: 160 }}>
                    {opp.potentialname}
                  </Text>
                }
                description={
                  <Space size="small">
                    <Tag color={getStageColor(opp.sales_stage)}>
                      {opp.sales_stage}
                    </Tag>
                    {opp.amount && (
                      <Text type="secondary">
                        ${Number(opp.amount).toLocaleString()}
                      </Text>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
          locale={{ emptyText: "No opportunities" }}
        />
      </Card>

      {/* Related Documents */}
      {currentAccountId && (
        <RelatedDocuments
          entityId={currentAccountId}
          entityModule="Accounts"
          title="Documents"
          compact
        />
      )}

      {/* Recent Activity */}
      <Card
        size="small"
        title="Upcoming Events"
        styles={{ body: { padding: 0 } }}
      >
        <List
          size="small"
          dataSource={activities}
          locale={{ emptyText: "No events" }}
          renderItem={(item) => {
            const eventDate = dayjs(item.date_start);
            const today = dayjs().startOf("day");
            const tomorrow = today.add(1, "day");

            let dateLabel = eventDate.format("MMM D");
            if (eventDate.isSame(today, "day")) dateLabel = "Today";
            else if (eventDate.isSame(tomorrow, "day")) dateLabel = "Tomorrow";

            const time = item.time_start
              ? dayjs(`${item.date_start} ${item.time_start}`).format("h:mm A")
              : "";

            return (
              <List.Item style={{ padding: "8px 12px" }}>
                <Text type="secondary" style={{ marginRight: 8, fontSize: 12 }}>
                  {dateLabel} {time}:
                </Text>
                <Text ellipsis>{item.subject}</Text>
              </List.Item>
            );
          }}
        />
      </Card>

      {/* Log Activity Modal */}
      <Modal
        title="Log Activity"
        open={activityModalOpen}
        onCancel={() => setActivityModalOpen(false)}
        footer={null}
      >
        <Form
          form={activityForm}
          layout="vertical"
          onFinish={handleLogActivity}
          initialValues={{
            activitytype: "Call",
            date_start: dayjs(),
          }}
        >
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: "Please enter a subject" }]}
          >
            <Input placeholder="e.g., Follow-up call about proposal" />
          </Form.Item>
          <Form.Item
            name="activitytype"
            label="Activity Type"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="Call">Call</Select.Option>
              <Select.Option value="Meeting">Meeting</Select.Option>
              <Select.Option value="Email">Email</Select.Option>
              <Select.Option value="Task">Task</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="date_start"
            label="Date & Time"
            rules={[{ required: true }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item name="description" label="Notes">
            <Input.TextArea rows={3} placeholder="Additional notes..." />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setActivityModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={activityLoading}
              >
                Log Activity
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Pipeline Modal */}
      <Modal
        title={
          account ? `Pipeline - ${account.accountname}` : "Pipeline Summary"
        }
        open={pipelineModalOpen}
        onCancel={() => setPipelineModalOpen(false)}
        footer={
          <Button onClick={() => setPipelineModalOpen(false)}>Close</Button>
        }
        width={600}
      >
        {pipelineLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : pipelineData ? (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Statistic
                  title="Total Deals"
                  value={pipelineData.totalDeals}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Total Value"
                  value={pipelineData.totalValue}
                  precision={2}
                  prefix="$"
                />
              </Col>
            </Row>
            <Descriptions title="By Stage" column={1} size="small" bordered>
              {Object.entries(pipelineData.byStage).map(
                ([stage, data]: [string, any]) => (
                  <Descriptions.Item key={stage} label={stage}>
                    {data.count} deals - ${data.value.toFixed(2)}
                  </Descriptions.Item>
                )
              )}
            </Descriptions>
            {pipelineData.opportunities.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Opportunities:</Text>
                <List
                  size="small"
                  dataSource={pipelineData.opportunities}
                  renderItem={(opp: any) => (
                    <List.Item>
                      <List.Item.Meta
                        title={opp.potentialname}
                        description={
                          <Space>
                            <Tag color={getStageColor(opp.sales_stage)}>
                              {opp.sales_stage}
                            </Tag>
                            <Text>
                              ${parseFloat(opp.amount || 0).toFixed(2)}
                            </Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        ) : (
          <Empty description="No pipeline data" />
        )}
      </Modal>

      {/* Forecast Modal */}
      <Modal
        title={
          account ? `Forecast - ${account.accountname}` : "Revenue Forecast"
        }
        open={forecastModalOpen}
        onCancel={() => setForecastModalOpen(false)}
        footer={
          <Button onClick={() => setForecastModalOpen(false)}>Close</Button>
        }
        width={600}
      >
        {forecastLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : forecastData ? (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Statistic
                  title="30 Days"
                  value={forecastData.next_30_days?.total || 0}
                  precision={2}
                  prefix="$"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="60 Days"
                  value={forecastData.next_60_days?.total || 0}
                  precision={2}
                  prefix="$"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="90 Days"
                  value={forecastData.next_90_days?.total || 0}
                  precision={2}
                  prefix="$"
                />
              </Col>
            </Row>
            {forecastData.deals && forecastData.deals.length > 0 && (
              <div>
                <Text strong>Upcoming Deals:</Text>
                <List
                  size="small"
                  dataSource={forecastData.deals}
                  renderItem={(deal: any) => (
                    <List.Item>
                      <List.Item.Meta
                        title={deal.potentialname}
                        description={
                          <Space>
                            <Text type="secondary">
                              Close: {dayjs(deal.closingdate).format("MMM D")}
                            </Text>
                            <Text>
                              ${parseFloat(deal.amount || 0).toFixed(2)}
                            </Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        ) : (
          <Empty description="No forecast data" />
        )}
      </Modal>
    </div>
  );
};

// Helper to get stage color
const getStageColor = (stage: string): string => {
  const colors: Record<string, string> = {
    Prospecting: "blue",
    Qualification: "cyan",
    "Needs Analysis": "geekblue",
    "Value Proposition": "purple",
    Negotiation: "orange",
    "Closed Won": "green",
    "Closed Lost": "red",
  };
  return colors[stage] || "default";
};
